import { useEffect, useState } from "react";
import AccountDetails from "./AccountDetails";
import AddCompanyModal from "./AddCompanyModal";
import ConfirmModal from "./ConfirmModal";
import CustomNavbar from "../CustomPermissions/CustomNavbar";
import EditCompanyModal from "./EditCompanyModal"; // Import the EditCompanyModal
import { Link } from "react-router-dom";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

export default function HomePage() {
  const [companies, setCompanies] = useState([]);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // State for edit modal
  const [companyToEdit, setCompanyToEdit] = useState(null); // Track the company to edit

  // Fetch companies and their account details from the API
  const fetchCompany = () => {
    const accessToken = localStorage.getItem("access_token");
    fetch(getApiUrl("/PartnerFiles/companies/"), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch companies");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Fetched companies:", data.data);
        setCompanies(data.data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  // Toggle expanded rows for account details
  const toggleExpand = (companyId) => {
    setExpandedCompanyId(expandedCompanyId === companyId ? null : companyId);
  };

  // Handle adding a new company
  const handleAddCompany = (newCompany) => {
    setCompanies([...companies, newCompany]);
    setIsAddModalOpen(false);
    fetchCompany();
  };

  // Handle deleting a company
  const handleDeleteCompany = () => {
    if (!companyToDelete) return;
    const accessToken = localStorage.getItem("access_token");
    fetch(
      getApiUrl(`/PartnerFiles/companies/${companyToDelete.id}/`),
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to delete company");
        }
        alert("Company deleted successfully!");
        setCompanies(companies.filter((c) => c.id !== companyToDelete.id));
        setCompanyToDelete(null);
      })
      .catch((error) => console.error("Error deleting company:", error));
  };

  // Handle deleting an account
  const handleDeleteAccount = () => {
    if (!accountToDelete) return;
    const accessToken = localStorage.getItem("access_token");
    fetch(
      getApiUrl(`/PartnerFiles/account-details/${accountToDelete.id}/`),
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to delete account");
        }
        alert("Account deleted successfully!");
        setCompanies(
          companies.map((company) =>
            company.id === accountToDelete.company
              ? {
                  ...company,
                  account_details: company.account_details.filter(
                    (acc) => acc.id !== accountToDelete.id
                  ),
                }
              : company
          )
        );
        setAccountToDelete(null);
      })
      .catch((error) => console.error("Error deleting account:", error));
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-blue-50">
        <div className="text-blue-600 flex flex-col items-center">
          <svg
            className="animate-spin h-10 w-10 mb-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-lg font-medium">Loading data...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-blue-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          <div className="flex items-center text-red-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-bold">Error</h2>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={fetchCompany}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded transition duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <CustomNavbar />
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50"
        style={{ width: "100vw" }}
      >
        <div className="mx-auto">
          {/* Header */}
          <header className="mb-4">
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <h1 className="text-3xl font-bold text-blue-800 text-center">
                Company Management Dashboard
              </h1>
              <p className="text-center text-blue-600 mt-2">
                Manage your companies and account details in one place
              </p>
            </div>
            {/* Add Company Button */}
            <div style={{ width: "100vw" }}>
              <div style={{ display: "flex", width: "100vw", justifyContent: "space-around" }}>
                <div className="flex justify-center mt-2">
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="group relative inline-flex items-center px-3 py-2 bg-blue-500 text-white font-medium text-base rounded-lg overflow-hidden shadow-md hover:bg-blue-600 transition-all duration-300 ease-out"
                  >
                    <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-500 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
                    <span className="relative flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Add New Company
                    </span>
                  </button>
                </div>

                <div
                  style={{ display: "flex", justifyContent: "flex-end", width: "20%", gap: '20px' }}
                >
                  <Link
                    to="/AccountChequeList"
                    style={{
                      textDecoration: "none",
                      color: "#0077B6",
                      marginTop: "10px",
                      padding: "15px 25px",
                    }}
                    className="bg-blue-500 text-white rounded font-bold"
                  >
                    Cheque
                  </Link>

                  <Link
                    to="/AccountATMList"
                    style={{
                      textDecoration: "none",
                      color: "#0077B6",
                      marginTop: "10px",
                      padding: "15px 25px",
                    }}
                    className="bg-blue-500 text-white rounded font-bold"
                  >
                    ATM
                  </Link>
                </div>
              </div>
            </div>
          </header>

          {/* Companies List */}
          <div className="space-y-5" style={{ width: "80%", margin: "0 auto" }}>
            {companies.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 text-blue-300 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  No Companies Found
                </h3>
                <p className="text-gray-500 mb-4">
                  Add your first company to get started
                </p>
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add Company
                </button>
              </div>
            ) : (
              companies.map((company) => (
                <div
                  key={company.id}
                  className="bg-white rounded-xl shadow-sm  transition-all duration-300 hover:shadow-md"
                >
                  <div className="p-5 border-b border-blue-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-blue-800">
                            {company.name}
                          </h2>
                          <p className="text-blue-500 text-sm mt-1">
                            {company.desc}
                          </p>
                          <div className="flex items-center mt-2">
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                              {company.account_details?.length || 0} Accounts
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 md:justify-end">
                        <button
                          onClick={() => toggleExpand(company.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors duration-200 ${
                            expandedCompanyId === company.id
                              ? "bg-blue-100 text-blue-700"
                              : "bg-blue-500 text-white hover:bg-blue-600"
                          }`}
                        >
                          {expandedCompanyId === company.id ? (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                />
                              </svg>
                              Hide Details
                            </>
                          ) : (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                              View Details
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setCompanyToDelete(company);
                            setConfirmAction("deleteCompany");
                          }}
                          className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-medium flex items-center transition-colors duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </button>
                        <button
                          onClick={() => {
                            setCompanyToEdit(company); // Set the company to edit
                            setIsEditModalOpen(true); // Open the edit modal
                          }}
                          className="px-4 py-2 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 rounded-lg text-sm font-medium flex items-center transition-colors duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Expanded Account Details */}
                  {expandedCompanyId === company.id && (
                    <div className="bg-blue-50 p-3 py-2 transition-all duration-300 ease-in-out">
                      <AccountDetails
                        accounts={company.account_details}
                        companyId={company.id}
                        onRefresh={fetchCompany}
                        onDeleteAccount={(account) => {
                          setAccountToDelete(account);
                          setConfirmAction("deleteAccount");
                        }}
                        onAddAccount={(newAccount) => {
                          // Add the new account to the state
                          setCompanies(
                            companies.map((c) =>
                              c.id === company.id
                                ? {
                                    ...c,
                                    account_details: [
                                      ...c.account_details,
                                      { ...newAccount, id: Date.now() },
                                    ],
                                  }
                                : c
                            )
                          );
                          // Make an API call to save the new account
                          fetch(
                            getApiUrl("/PartnerFiles/account-details/"),
                            {
                              method: "POST",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${localStorage.getItem(
                                  "access_token"
                                )}`,
                              },
                              body: JSON.stringify(newAccount),
                            }
                          )
                            .then((response) => {
                              if (!response.ok)
                                throw new Error("Failed to add account");
                              alert("Account added successfully!");
                            })
                            .catch((error) =>
                              console.error("Error adding account:", error)
                            );
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal for adding a new company */}
        {isAddModalOpen && (
          <AddCompanyModal
            onClose={() => setIsAddModalOpen(false)}
            onAddCompany={handleAddCompany}
          />
        )}

        {/* Confirmation Modal for Delete Actions */}
        {confirmAction === "deleteCompany" && (
          <ConfirmModal
            message={`Are you sure you want to delete the company "${companyToDelete?.name}"?`}
            onConfirm={() => {
              handleDeleteCompany();
              setConfirmAction(null);
            }}
            onCancel={() => {
              setCompanyToDelete(null);
              setConfirmAction(null);
            }}
          />
        )}
        {confirmAction === "deleteAccount" && (
          <ConfirmModal
            message={`Are you sure you want to delete the account "${accountToDelete?.acc_name}"?`}
            onConfirm={() => {
              handleDeleteAccount();
              setConfirmAction(null);
            }}
            onCancel={() => {
              setAccountToDelete(null);
              setConfirmAction(null);
            }}
          />
        )}

        {/* Edit Company Modal */}
        {isEditModalOpen && companyToEdit && (
          <EditCompanyModal
            company={companyToEdit}
            onClose={() => {
              setIsEditModalOpen(false);
              setCompanyToEdit(null);
              fetchCompany();
            }}
          />
        )}
      </div>
    </div>
  );
}
